/**
 * Режим «эта сделка» (Этап 5.2/5.3): приложение открыто из карточки сделки Битрикса
 * (placement CRM_DEAL_DETAIL_TAB). Показываем карточку именно этой сделки, а не общий
 * список. Если сделка ещё не перенесена в календарь (не дошла до триггер-стадии:
 * «Предоплата получена» в обычной воронке или «Счёт выставлен» в воронке «Ивент») —
 * поясняем и даём перейти в общий календарь/Гант.
 */
import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { DealEditor } from '@/components/modals/DealEditor';
import { CalendarRange, Inbox, Loader2 } from 'lucide-react';
import { Toasts } from '@/components/ui/Toasts';
import { ModalsRoot } from '@/components/modals/ModalsRoot';
import { syncOneDealNow } from '@/data/syncClient';
import { startOfDay } from '@/utils/time';

export function BitrixDealView({ dealId }: { dealId: string }) {
  const deals = useStore((s) => s.deals);
  const reload = useStore((s) => s.reload);
  const clearBitrixDeal = useStore((s) => s.clearBitrixDeal);
  const setPage = useStore((s) => s.setPage);
  const setPendingCal = useStore((s) => s.setPendingCal);
  const found = deals.some((d) => d.id === dealId);
  const [syncing, setSyncing] = useState(false);

  // См. index.css (.bx-natural-height): пока показан простой экран-заглушка (не
  // сама карточка игры), снимаем принудительную высоту 100% с html/body/#root —
  // иначе BX24.fitWindow() не может уменьшить фрейм Битрикса под реальный контент.
  useEffect(() => {
    if (found) return;
    document.documentElement.classList.add('bx-natural-height');
    return () => document.documentElement.classList.remove('bx-natural-height');
  }, [found]);

  // Если сделки ещё нет в календаре — пробуем подтянуть её из Битрикса сразу,
  // не дожидаясь фонового опроса (раз в минуту). Так менеджер не ждёт.
  useEffect(() => {
    if (found) return;
    let cancelled = false;
    setSyncing(true);
    syncOneDealNow(dealId)
      .catch(() => null)
      .then(() => reload())
      .finally(() => { if (!cancelled) setSyncing(false); });
    return () => { cancelled = true; };
  }, [dealId, found, reload]);

  // Перейти в общий интерфейс (Гант/календарь) — выходим из режима «эта сделка».
  // Открываем сразу вид «Ресурсы» (Гант) и, если у события есть дата, — на его день.
  const toCalendar = () => {
    const d = deals.find((x) => x.id === dealId);
    const ts = d?.event.start_at ?? d?.event.event_date;
    setPendingCal('resources', ts != null ? startOfDay(ts) : null);
    clearBitrixDeal();
    setPage('calendar');
  };

  if (found) {
    return (
      <div className="h-full flex flex-col bg-surface-0 text-ink-1 overflow-hidden">
        <div className="h-10 px-4 flex items-center justify-between border-b border-line bg-surface-1 flex-shrink-0">
          <span className="text-xs text-ink-3">Сделка из Битрикса</span>
          <button className="btn-soft !h-7 text-xs" onClick={toCalendar}>
            <CalendarRange className="w-3.5 h-3.5" /> Открыть в календаре (Гант)
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <DealEditor chrome="page" id={dealId} onClose={toCalendar} />
        </div>
        <Toasts />
        <ModalsRoot />
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="flex items-center justify-center bg-surface-0 text-ink-2 p-10">
        <div className="text-center space-y-3">
          <Loader2 className="w-7 h-7 text-accent animate-spin mx-auto" />
          <div className="text-sm text-ink-3">Загружаем сделку из Битрикса…</div>
        </div>
        <Toasts />
      </div>
    );
  }

  // Без h-full: этот блок — просто статус-заглушка, не весь экран приложения.
  // h-full растягивал бы его на всю текущую высоту фрейма Битрикса (html/body/#root
  // заданы height:100%), из-за чего BX24.fitWindow() не мог уменьшить фрейм под
  // реальную высоту контента — получался замкнутый круг (см. providers/bitrixClient.ts).
  return (
    <div className="flex items-center justify-center bg-surface-0 text-ink-2 p-10">
      <div className="max-w-md text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-surface-2 grid place-items-center mx-auto">
          <Inbox className="w-7 h-7 text-ink-3" />
        </div>
        <div className="text-lg font-semibold text-ink-1">Сделка ещё не в календаре</div>
        <p className="text-sm leading-relaxed">
          Событие по этой сделке появится автоматически, когда она дойдёт до нужной стадии:
          <b> «Счёт выставлен»</b> в воронке «Ивент» или <b>«Предоплата получена»</b> в обычной
          воронке. Тогда здесь откроется карточка игры.
        </p>
        <button className="btn-primary !h-10 mx-auto" onClick={toCalendar}>
          <CalendarRange className="w-4 h-4" /> Открыть общий календарь
        </button>
      </div>
      <Toasts />
      <ModalsRoot />
    </div>
  );
}
